import React, { useState } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Typography, 
  Calendar, 
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Radio,
  Alert,
  Badge
} from 'antd';
import { 
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  BookOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { Header } from '../../components/common/layout';
import dayjs from 'dayjs';

const { Title, Paragraph, Text } = Typography;

const WorkshopPage = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const workshopCategories = {
    'Gardening & Landscaping': [
      {
        id: 'basic-gardening',
        title: 'Basic Gardening Fundamentals',
        description: 'Learn essential gardening skills including soil preparation, planting, watering, and basic plant care.',
        duration: '2 hours',
        price: 45,
        maxStudents: 12,
        instructor: 'Sarah Green',
        level: 'Beginner',
        tools: 'All tools provided',
        date: '2024-01-15',
        time: '10:00 AM'
      },
      {
        id: 'lawn-care',
        title: 'Professional Lawn Care',
        description: 'Master lawn maintenance including mowing techniques, fertilizing, weed control, and seasonal care.',
        duration: '3 hours',
        price: 65,
        maxStudents: 10,
        instructor: 'Mike Johnson',
        level: 'Intermediate',
        tools: 'Bring work gloves',
        date: '2024-01-20',
        time: '9:00 AM'
      },
      {
        id: 'landscape-design',
        title: 'Landscape Design Basics',
        description: 'Learn to design beautiful outdoor spaces with proper plant selection and layout principles.',
        duration: '4 hours',
        price: 85,
        maxStudents: 8,
        instructor: 'Lisa Chen',
        level: 'Intermediate',
        tools: 'Sketch pad and pencils provided',
        date: '2024-01-25',
        time: '1:00 PM'
      }
    ],
    'Home Repairs': [
      {
        id: 'drywall-patching',
        title: 'Drywall Patching & Repair',
        description: 'Learn to patch holes, fix cracks, and achieve smooth wall finishes like a professional.',
        duration: '2.5 hours',
        price: 55,
        maxStudents: 10,
        instructor: 'Tom Rodriguez',
        level: 'Beginner',
        tools: 'All materials provided',
        date: '2024-01-18',
        time: '6:00 PM'
      },
      {
        id: 'basic-plumbing',
        title: 'Basic Plumbing Repairs',
        description: 'Fix leaky faucets, unclog drains, and handle common plumbing issues safely.',
        duration: '3 hours',
        price: 75,
        maxStudents: 8,
        instructor: 'Dave Wilson',
        level: 'Beginner',
        tools: 'Basic tools provided',
        date: '2024-01-22',
        time: '6:00 PM'
      },
      {
        id: 'electrical-basics',
        title: 'Electrical Safety & Basic Repairs',
        description: 'Learn electrical safety, replace outlets, switches, and handle simple electrical tasks.',
        duration: '4 hours',
        price: 95,
        maxStudents: 6,
        instructor: 'Carlos Martinez',
        level: 'Intermediate',
        tools: 'Safety equipment provided',
        date: '2024-01-28',
        time: '9:00 AM'
      }
    ],
    'Painting & Finishing': [
      {
        id: 'interior-painting',
        title: 'Professional Interior Painting',
        description: 'Master painting techniques, color selection, and achieve professional-quality finishes.',
        duration: '3 hours',
        price: 60,
        maxStudents: 12,
        instructor: 'Jennifer Lee',
        level: 'Beginner',
        tools: 'Brushes and rollers provided',
        date: '2024-01-16',
        time: '10:00 AM'
      },
      {
        id: 'exterior-painting',
        title: 'Exterior Painting & Weatherproofing',
        description: 'Learn exterior painting techniques, surface preparation, and weather protection.',
        duration: '4 hours',
        price: 80,
        maxStudents: 8,
        instructor: 'Robert Kim',
        level: 'Intermediate',
        tools: 'Ladders and safety gear provided',
        date: '2024-01-24',
        time: '8:00 AM'
      }
    ],
    'Cleaning & Maintenance': [
      {
        id: 'deep-cleaning',
        title: 'Deep Cleaning Techniques',
        description: 'Learn professional cleaning methods for every room and surface in your home.',
        duration: '2 hours',
        price: 40,
        maxStudents: 15,
        instructor: 'Maria Garcia',
        level: 'Beginner',
        tools: 'Cleaning supplies provided',
        date: '2024-01-19',
        time: '2:00 PM'
      },
      {
        id: 'appliance-maintenance',
        title: 'Appliance Maintenance & Care',
        description: 'Keep your appliances running efficiently with proper maintenance and troubleshooting.',
        duration: '2.5 hours',
        price: 50,
        maxStudents: 10,
        instructor: 'Steve Thompson',
        level: 'Beginner',
        tools: 'Maintenance kits provided',
        date: '2024-01-26',
        time: '6:00 PM'
      }
    ],
    'Seasonal Maintenance': [
      {
        id: 'winter-prep',
        title: 'Winter Home Preparation',
        description: 'Prepare your home for winter with insulation, weatherproofing, and heating system checks.',
        duration: '3 hours',
        price: 70,
        maxStudents: 10,
        instructor: 'Patricia Brown',
        level: 'Beginner',
        tools: 'Weatherproofing materials provided',
        date: '2024-01-21',
        time: '10:00 AM'
      },
      {
        id: 'spring-maintenance',
        title: 'Spring Home Maintenance',
        description: 'Complete spring checklist including gutter cleaning, AC preparation, and exterior inspection.',
        duration: '3.5 hours',
        price: 75,
        maxStudents: 8,
        instructor: 'James Wilson',
        level: 'Intermediate',
        tools: 'Inspection tools provided',
        date: '2024-01-27',
        time: '9:00 AM'
      }
    ]
  };

  const getAllWorkshops = () => {
    return Object.values(workshopCategories).flat();
  };

  const getWorkshopsForDate = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    return getAllWorkshops().filter(workshop => workshop.date === dateStr);
  };

  const getDateCellRender = (date) => {
    const workshops = getWorkshopsForDate(date);
    return (
      <div>
        {workshops.map(workshop => (
          <Badge
            key={workshop.id}
            count={1}
            style={{ backgroundColor: '#52c41a', marginBottom: 2 }}
          >
            <div
              style={{
                fontSize: '10px',
                padding: '1px 4px',
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '2px',
                cursor: 'pointer',
                marginBottom: '1px'
              }}
              onClick={() => handleWorkshopClick(workshop)}
            >
              {workshop.title}
            </div>
          </Badge>
        ))}
      </div>
    );
  };

  const handleWorkshopClick = (workshop) => {
    setSelectedWorkshop(workshop);
    setIsModalVisible(true);
  };

  const handleRegister = (values) => {
    Modal.success({
      title: 'Registration Successful!',
      content: (
        <div>
          <p>You've successfully registered for <strong>{selectedWorkshop.title}</strong></p>
          <p><strong>Date:</strong> {selectedWorkshop.date}</p>
          <p><strong>Time:</strong> {selectedWorkshop.time}</p>
          <p><strong>Price:</strong> ${selectedWorkshop.price}</p>
          <p>You'll receive a confirmation email with workshop details and location information.</p>
        </div>
      ),
      onOk: () => {
        setIsModalVisible(false);
        form.resetFields();
      }
    });
  };

  const renderWorkshopModal = () => (
    <Modal
      title={
        <div>
          <BookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Workshop Registration
        </div>
      }
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      footer={null}
      width={600}
    >
      {selectedWorkshop && (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <Title level={4}>{selectedWorkshop.title}</Title>
            <Paragraph>{selectedWorkshop.description}</Paragraph>
            
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Space>
                  <CalendarOutlined />
                  <Text strong>Date:</Text>
                  <Text>{dayjs(selectedWorkshop.date).format('MMMM DD, YYYY')}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <ClockCircleOutlined />
                  <Text strong>Time:</Text>
                  <Text>{selectedWorkshop.time}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <UserOutlined />
                  <Text strong>Instructor:</Text>
                  <Text>{selectedWorkshop.instructor}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <DollarOutlined />
                  <Text strong>Price:</Text>
                  <Text style={{ color: '#52c41a', fontSize: '16px', fontWeight: 'bold' }}>
                    ${selectedWorkshop.price}
                  </Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <ClockCircleOutlined />
                  <Text strong>Duration:</Text>
                  <Text>{selectedWorkshop.duration}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space>
                  <UserOutlined />
                  <Text strong>Max Students:</Text>
                  <Text>{selectedWorkshop.maxStudents}</Text>
                </Space>
              </Col>
              <Col span={24}>
                <Space>
                  <Tag color="blue">{selectedWorkshop.level}</Tag>
                  <Text strong>Tools:</Text>
                  <Text>{selectedWorkshop.tools}</Text>
                </Space>
              </Col>
            </Row>
          </Card>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleRegister}
          >
            <Title level={5}>Registration Information</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter your first name' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter your last name' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[{ required: true, message: 'Please enter your phone number' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="experience"
              label="Experience Level"
              rules={[{ required: true, message: 'Please select your experience level' }]}
            >
              <Radio.Group>
                <Radio value="beginner">Beginner</Radio>
                <Radio value="intermediate">Intermediate</Radio>
                <Radio value="advanced">Advanced</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="dietary"
              label="Dietary Restrictions (for refreshments)"
            >
              <Input placeholder="Any allergies or dietary restrictions?" />
            </Form.Item>

            <Form.Item
              name="emergency"
              label="Emergency Contact"
              rules={[{ required: true, message: 'Please provide emergency contact' }]}
            >
              <Input placeholder="Name and phone number" />
            </Form.Item>

            <Alert
              message="Payment Information"
              description="Payment will be processed securely after registration. You'll receive a confirmation email with payment details."
              type="info"
              style={{ marginBottom: 16 }}
            />

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Button onClick={() => setIsModalVisible(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" size="large">
                  Register for Workshop - ${selectedWorkshop.price}
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      )}
    </Modal>
  );

  return (
    <>
      <Header />
      <div style={{ padding: '40px 0', marginTop: '80px' }}>
      {/* Hero Section */}
      <section style={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
        padding: '60px 0',
        color: 'white',
        marginBottom: '3rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={1} style={{ color: 'white', marginBottom: '1rem' }}>
              Home Maintenance Workshops
            </Title>
            <Paragraph style={{ color: 'white', fontSize: '1.3rem', marginBottom: '2rem' }}>
              Learn essential home maintenance skills from our expert instructors
            </Paragraph>
            <Button 
              type="primary" 
              size="large"
              style={{ 
                background: 'white',
                color: theme.colors.primary,
                border: 'none',
                borderRadius: '25px',
                height: '50px',
                padding: '0 30px',
                fontSize: '1.1rem',
                fontWeight: '600',
                fontFamily: theme.fonts.primary
              }}
            >
              <Link to="/signup">Sign Up for Workshops</Link>
            </Button>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>

        <Row gutter={[32, 32]}>
          {/* Calendar */}
          <Col xs={24} lg={16}>
            <Card 
              title="Workshop Calendar" 
              style={{ 
                marginBottom: '2rem',
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.md,
                border: `1px solid ${theme.colors.borderGray}`
              }}
              headStyle={{
                fontFamily: theme.fonts.primary,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: theme.colors.primary
              }}
            >
              <Calendar
                value={selectedDate}
                onChange={setSelectedDate}
                dateCellRender={getDateCellRender}
                style={{ minHeight: '400px' }}
              />
            </Card>

            {/* Workshop Categories */}
            <Card 
              title="Workshop Categories"
              style={{
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.md,
                border: `1px solid ${theme.colors.borderGray}`
              }}
              headStyle={{
                fontFamily: theme.fonts.primary,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: theme.colors.primary
              }}
            >
              {Object.entries(workshopCategories).map(([category, workshops]) => (
                <div key={category} style={{ marginBottom: '2rem' }}>
                  <Title level={4} style={{ 
                    marginBottom: '1rem',
                    color: theme.colors.primary,
                    fontFamily: theme.fonts.primary
                  }}>
                    {category}
                  </Title>
                  <Row gutter={[16, 16]}>
                    {workshops.map(workshop => (
                      <Col xs={24} sm={12} key={workshop.id}>
                        <Card
                          size="small"
                          hoverable
                          onClick={() => handleWorkshopClick(workshop)}
                          style={{ 
                            cursor: 'pointer',
                            borderRadius: theme.borderRadius.md,
                            boxShadow: theme.shadows.sm,
                            border: `1px solid ${theme.colors.borderGray}`,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                            <Title level={5} style={{ margin: 0, fontSize: '1rem' }}>
                              {workshop.title}
                            </Title>
                            <Tag color="green" style={{ 
                              margin: 0,
                              backgroundColor: theme.colors.success,
                              color: 'white',
                              border: 'none'
                            }}>
                              ${workshop.price}
                            </Tag>
                          </div>
                          <Paragraph style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                            {workshop.description}
                          </Paragraph>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Space size="small">
                              <Tag color="blue" style={{
                                backgroundColor: theme.colors.primary,
                                color: 'white',
                                border: 'none'
                              }}>{workshop.level}</Tag>
                              <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                                {workshop.duration}
                              </Text>
                            </Space>
                            <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                              {dayjs(workshop.date).format('MMM DD')} at {workshop.time}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </Card>
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            <Card 
              title="Workshop Benefits" 
              style={{ 
                marginBottom: '2rem',
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.md,
                border: `1px solid ${theme.colors.borderGray}`
              }}
              headStyle={{
                fontFamily: theme.fonts.primary,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: theme.colors.primary
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <ToolOutlined style={{ color: theme.colors.primary, marginRight: 8 }} />
                  <Text strong style={{ fontFamily: theme.fonts.primary }}>Hands-on Learning</Text>
                  <br />
                  <Text type="secondary">Practice with real tools and materials</Text>
                </div>
                <div>
                  <UserOutlined style={{ color: theme.colors.primary, marginRight: 8 }} />
                  <Text strong style={{ fontFamily: theme.fonts.primary }}>Expert Instructors</Text>
                  <br />
                  <Text type="secondary">Learn from certified professionals</Text>
                </div>
                <div>
                  <EnvironmentOutlined style={{ color: theme.colors.primary, marginRight: 8 }} />
                  <Text strong style={{ fontFamily: theme.fonts.primary }}>Small Class Sizes</Text>
                  <br />
                  <Text type="secondary">Maximum 15 students per workshop</Text>
                </div>
                <div>
                  <DollarOutlined style={{ color: theme.colors.primary, marginRight: 8 }} />
                  <Text strong style={{ fontFamily: theme.fonts.primary }}>All Materials Included</Text>
                  <br />
                  <Text type="secondary">Tools and supplies provided</Text>
                </div>
              </Space>
            </Card>

            <Card 
              title="Upcoming Workshops"
              style={{
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows.md,
                border: `1px solid ${theme.colors.borderGray}`
              }}
              headStyle={{
                fontFamily: theme.fonts.primary,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: theme.colors.primary
              }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {getAllWorkshops()
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .slice(0, 5)
                  .map(workshop => (
                    <div
                      key={workshop.id}
                      style={{
                        padding: '8px',
                        border: `1px solid ${theme.colors.borderGray}`,
                        borderRadius: theme.borderRadius.sm,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handleWorkshopClick(workshop)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = theme.colors.lightGray;
                        e.target.style.borderColor = theme.colors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.borderColor = theme.colors.borderGray;
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ fontSize: '0.9rem', fontFamily: theme.fonts.primary }}>
                          {workshop.title}
                        </Text>
                        <Text style={{ color: theme.colors.success, fontWeight: 'bold' }}>
                          ${workshop.price}
                        </Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                        {dayjs(workshop.date).format('MMM DD')} at {workshop.time}
                      </Text>
                    </div>
                  ))}
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {renderWorkshopModal()}
    </div>
    </>
  );
};

export default WorkshopPage;
